import { useQuery } from '@tanstack/react-query';

import type { DateRange } from '@/components/ui/FilterBar';
import { env } from '@/env';

import type { DashboardData } from './dashboardData';
import { sampleDashboard } from './sampleDashboard';

/**
 * The range is spelled out as dates, not as the preset's name, so "Last 7
 * days" chosen on two different days is two different queries.
 */
export function dashboardKey(range: DateRange) {
  return ['dashboard', 'summary', range.from, range.to] as const;
}

/**
 * Where the numbers come from, known before they arrive, so the sample notice
 * is on the page from the first moment rather than appearing with the data
 * and pushing everything down. Becomes 'live' with the real API.
 */
export const DASHBOARD_SOURCE: DashboardData['source'] = 'sample';

/**
 * Long enough that the loading state is seen, so it is known to work before
 * real requests make it matter. None under test, where it only slows the run.
 */
const SAMPLE_PAUSE_MS = env.isTest ? 0 : 400;

async function loadDashboard(range: DateRange): Promise<DashboardData> {
  if (SAMPLE_PAUSE_MS > 0) {
    await new Promise((resolve) => setTimeout(resolve, SAMPLE_PAUSE_MS));
  }
  return sampleDashboard(range);
}

/**
 * The one seam between the dashboard and its numbers.
 *
 * Today it returns sample data. When the lead's dashboard API lands (Day 79,
 * or an early numbers API by Day 38), this `queryFn` calls the generated
 * client and maps the reply into `DashboardData` with `source: 'live'`; the
 * page and everything on it stay as they are.
 */
export function useDashboard(range: DateRange) {
  return useQuery({
    queryKey: dashboardKey(range),
    queryFn: () => loadDashboard(range),
  });
}
